import { ANALYTICS_EVENT_NAMES } from "analytics/catalog/events";
import { ScreenKey } from "analytics/catalog/screens";
import { buildCommonEventProps } from "analytics/core/commonProps";
import { trackEvent } from "analytics/core/tracker";

export type SelfCampaignTimelineSelection =
  | "within_1_week"
  | "within_30_days"
  | "thirty_plus_days"
  | "skip";

export type SelfCampaignTimelineAnalyticsContext = {
  pathname: string;
  search: string;
  businessId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  planType?: string | null;
  currency?: string | null;
  leadSubmissionId?: string | null;
  partner?: string | null;
};

export type SelfCampaignTimelineSelectedProps = {
  timeline: SelfCampaignTimelineSelection;
};

const resolvePartner = (partner?: string | null): string | null => {
  if (typeof partner !== "string") {
    return null;
  }

  const trimmed = partner.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const resolveUserFlow = (partner: string | null): "partner" | "self-campaign" => {
  return partner ? "partner" : "self-campaign";
};

const buildTimelineProps = (
  context: SelfCampaignTimelineAnalyticsContext,
  screenKey: ScreenKey,
  extraProps: Record<string, unknown> = {},
): Record<string, unknown> => {
  const partner = resolvePartner(context.partner);

  return {
    ...buildCommonEventProps({
      screenKey,
      pathname: context.pathname,
      search: context.search,
      businessId: context.businessId ?? null,
      userId: context.userId ?? null,
      userEmail: context.userEmail ?? null,
      planType: context.planType ?? null,
      currency: context.currency ?? null,
    }),
    lead_submission_id: context.leadSubmissionId ?? null,
    partner,
    user_flow: resolveUserFlow(partner),
    ...extraProps,
  };
};

export const trackSelfCampaignTimelineStepViewed = (
  context: SelfCampaignTimelineAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignTimelineStepViewed,
    buildTimelineProps(context, "self_campaign_timeline_step"),
  );
};

export const trackSelfCampaignTimelineSelected = (
  context: SelfCampaignTimelineAnalyticsContext,
  props: SelfCampaignTimelineSelectedProps,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignTimelineSelected,
    buildTimelineProps(context, "self_campaign_timeline_step", {
      timeline: props.timeline,
    }),
  );
};

export const trackSelfCampaignTimelineSkipped = (
  context: SelfCampaignTimelineAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignTimelineSkipped,
    buildTimelineProps(context, "self_campaign_timeline_step", {
      timeline: "skip",
    }),
  );
};

export const trackSelfCampaignTimelineCalendlyRedirected = (
  context: SelfCampaignTimelineAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignTimelineCalendlyRedirected,
    buildTimelineProps(context, "self_campaign_timeline_step", {
      timeline: "within_30_days",
    }),
  );
};

export const trackSelfCampaignTimelineConfirmationViewed = (
  context: SelfCampaignTimelineAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignTimelineConfirmationViewed,
    buildTimelineProps(context, "self_campaign_timeline_confirmation", {
      timeline: "within_1_week",
    }),
  );
};
